import { PDFDocument, PDFPage } from 'pdf-lib'
import { pdfjs } from 'utils/pdfWorker'

class SsmPdfValidationError extends Error {}

const checkPageLimit = (current: number, added: number, maxPages: number) => {
  if (added === 0) throw new SsmPdfValidationError('The PDF contains no pages.')
  if (current + added > maxPages) {
    throw new SsmPdfValidationError(`The merged PDF exceeds ${maxPages} pages. Please reduce the number of files or pages.`)
  }
}

export async function appendSsmPdfPages(destination: PDFDocument, file: File, maxPages = 25): Promise<void> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let copiedPages: PDFPage[] | undefined
  try {
    const source = await PDFDocument.load(bytes)
    checkPageLimit(destination.getPageCount(), source.getPageCount(), maxPages)
    copiedPages = await destination.copyPages(source, source.getPageIndices())
  } catch (error) {
    if (error instanceof SsmPdfValidationError) throw error
    // Viewer-readable PDFs can contain encryption or structures pdf-lib cannot copy.
  }
  if (copiedPages) {
    copiedPages.forEach((page) => destination.addPage(page))
    return
  }

  const loadingTask = pdfjs.getDocument({ data: bytes.slice(), useSystemFonts: true, isEvalSupported: false })
  try {
    const source = await loadingTask.promise
    checkPageLimit(destination.getPageCount(), source.numPages, maxPages)
    for (let index = 1; index <= source.numPages; index++) {
      const sourcePage = await source.getPage(index)
      const size = sourcePage.getViewport({ scale: 1 })
      const viewport = sourcePage.getViewport({ scale: Math.min(2, 1600 / Math.max(size.width, size.height)) })
      const canvas = document.createElement('canvas')
      try {
        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        const context = canvas.getContext('2d')
        if (!context) throw new Error('Unable to render this PDF. Please try again in a supported browser.')
        await sourcePage.render({ canvasContext: context, viewport, background: '#ffffff' }).promise
        const image = await destination.embedPng(canvas.toDataURL('image/png'))
        const page = destination.addPage([size.width, size.height])
        page.drawImage(image, { x: 0, y: 0, width: size.width, height: size.height })
      } finally {
        canvas.width = 0
        canvas.height = 0
        sourcePage.cleanup()
      }
    }
  } catch (error) {
    if ((error as Error)?.name === 'PasswordException') {
      throw new Error('This PDF requires an opening password. Please upload an unlocked copy.')
    }
    throw error
  } finally {
    await loadingTask.destroy()
  }
}
